import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';

import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import { useSelectedOrganization } from '../../hooks/useSelectedOrganization';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import './Settings.css';

const LocationEditor = ({ userOrganizationId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin, isLoading: isUserLoading } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: locationId } = useParams();
  const activeOrganizationId = useSelectedOrganization(userOrganizationId);
  const notification = useNotification();

  const isEditMode = !!locationId;
  const locationData = location.state?.location;

  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Form fields
  const [storeName, setStoreName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('US');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [managerFirstName, setManagerFirstName] = useState('');
  const [managerLastName, setManagerLastName] = useState('');
  const [managerEmail, setManagerEmail] = useState('');
  const [managerPhone, setManagerPhone] = useState('');

  useEffect(() => {
    if (!isUserLoading && !isAdmin()) {
      notification.error('You do not have permission to access this page');
      navigate('/dashboard');
    }
  }, [isAdmin, isUserLoading, navigate, notification]);

  useEffect(() => {
    // If in edit mode, check if we have location data from navigation state
    if (isEditMode) {
      if (locationData) {
        // Populate form from navigation state
        setStoreName(locationData.store_name || '');
        setAddressLine1(locationData.address_line1 || '');
        setAddressLine2(locationData.address_line2 || '');
        setCity(locationData.city || '');
        setState(locationData.state || '');
        setPostalCode(locationData.postal_code || '');
        setCountry(locationData.country || 'US');
        setPhone(locationData.phone_number || '');
        setEmail(locationData.email || '');
        setWebsite(locationData.store_website || '');
        setManagerFirstName(locationData.manager_first_name || '');
        setManagerLastName(locationData.manager_last_name || '');
        setManagerEmail(locationData.manager_email || '');
        setManagerPhone(locationData.manager_phone_number || '');
      } else {
        // No location data in state - redirect back to list
        notification.error('Location data not found');
        navigate('/settings/locations');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, locationData]);

  const handleCancel = () => {
    navigate('/settings/locations');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!storeName.trim() || !addressLine1.trim() || !city.trim() || !state.trim() || !postalCode.trim()) {
      notification.error('Please fill in all required fields');
      return;
    }

    try {
      setIsSaving(true);
      const token = await getAccessTokenSilently();

      const locationData = {
        store_name: storeName,
        address_line1: addressLine1,
        address_line2: addressLine2,
        city,
        state,
        postal_code: postalCode,
        country,
        phone_number: phone,
        email,
        store_website: website,
        manager_first_name: managerFirstName,
        manager_last_name: managerLastName,
        manager_email: managerEmail,
        manager_phone_number: managerPhone,
        organization_id: activeOrganizationId
      };

      if (isEditMode) {
        await axios.patch(
          `${process.env.REACT_APP_SERVER_DOMAIN}/api/retailerlocations/${locationId}`,
          locationData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        notification.success('Location updated successfully');
      } else {
        await axios.post(
          `${process.env.REACT_APP_SERVER_DOMAIN}/api/retailerlocations`,
          locationData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        notification.success('Location created successfully');
      }

      navigate('/settings/locations');
    } catch (error) {
      console.error('Error saving location:', error);
      notification.error('Failed to save location');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    try {
      const token = await getAccessTokenSilently();
      await axios.delete(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/retailerlocations/${locationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notification.success('Location deleted successfully');
      navigate('/settings/locations');
    } catch (error) {
      console.error('Error deleting location:', error);
      notification.error('Failed to delete location');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const usStates = [
    { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
  ];

  const canadaProvinces = [
    { value: 'AB', label: 'Alberta' }, { value: 'BC', label: 'British Columbia' },
    { value: 'MB', label: 'Manitoba' }, { value: 'NB', label: 'New Brunswick' },
    { value: 'NL', label: 'Newfoundland and Labrador' }, { value: 'NS', label: 'Nova Scotia' },
    { value: 'ON', label: 'Ontario' }, { value: 'PE', label: 'Prince Edward Island' },
    { value: 'QC', label: 'Quebec' }, { value: 'SK', label: 'Saskatchewan' },
    { value: 'NT', label: 'Northwest Territories' }, { value: 'NU', label: 'Nunavut' },
    { value: 'YT', label: 'Yukon' }
  ];

  if (isUserLoading) {
    return (
      <div className="settings-page">
        <div className="settings-page-header">
          <div className="settings-page-header-left">
            <button className="settings-back-btn" onClick={handleCancel}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15,18 9,12 15,6" />
              </svg>
              Back
            </button>
            <h1 className="settings-page-title">
              {isEditMode ? 'Edit Location' : 'Create New Location'}
            </h1>
            <p className="settings-page-subtitle">Loading...</p>
          </div>
        </div>
        <div className="settings-page-content">
          <div className="settings-card">
            <div className="settings-loading">Loading location details...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Location"
        description="Are you sure you want to delete this location? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <div className="settings-page-header">
        <div className="settings-page-header-left">
          <button className="settings-back-btn" onClick={handleCancel}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15,18 9,12 15,6" />
            </svg>
            Back
          </button>
          <h1 className="settings-page-title">
            {isEditMode ? 'Edit Location' : 'Create New Location'}
          </h1>
          <p className="settings-page-subtitle">
            {isEditMode ? 'Update store location details' : 'Add a new store location to your organization'}
          </p>
        </div>
      </div>

      <div className="settings-page-content">
        <form onSubmit={handleSubmit} id="location-form">
          <div className="settings-card">
            <div className="settings-card-header">
              <h3 className="settings-card-title">Store Information</h3>
            </div>
            <div className="settings-card-body">
              <div className="settings-form-grid">
                <div className="settings-form-group full-width">
                  <label className="settings-form-label">
                    Store Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="settings-form-input"
                    value={storeName}
                    onChange={(e) => setStoreName(e.target.value)}
                    placeholder="Enter store name"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="settings-card">
            <div className="settings-card-header">
              <h3 className="settings-card-title">Address</h3>
            </div>
            <div className="settings-card-body">
              <div className="settings-form-grid">
                <div className="settings-form-group full-width">
                  <label className="settings-form-label">
                    Address Line 1 <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="settings-form-input"
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    placeholder="Street address"
                    required
                  />
                </div>
                <div className="settings-form-group full-width">
                  <label className="settings-form-label">Address Line 2</label>
                  <input
                    type="text"
                    className="settings-form-input"
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                    placeholder="Unit, Suite, Building"
                  />
                </div>
                <div className="settings-form-group">
                  <label className="settings-form-label">
                    City <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="settings-form-input"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    required
                  />
                </div>
                <div className="settings-form-group">
                  <label className="settings-form-label">
                    State/Province <span className="required">*</span>
                  </label>
                  <select
                    className="settings-form-select"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    required
                  >
                    <option value="">Select state/province</option>
                    {(country === 'US' ? usStates : canadaProvinces).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="settings-form-group">
                  <label className="settings-form-label">
                    Postal Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="settings-form-input"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="Postal code"
                    required
                  />
                </div>
                <div className="settings-form-group">
                  <label className="settings-form-label">Country</label>
                  <select
                    className="settings-form-select"
                    value={country}
                    onChange={(e) => { setCountry(e.target.value); setState(''); }}
                  >
                    <option value="US">United States</option>
                    <option value="Canada">Canada</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="settings-card">
            <div className="settings-card-header">
              <h3 className="settings-card-title">Contact Information</h3>
            </div>
            <div className="settings-card-body">
              <div className="settings-form-grid">
                <div className="settings-form-group">
                  <label className="settings-form-label">Phone</label>
                  <input
                    type="tel"
                    className="settings-form-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(123) 456-7890"
                  />
                </div>
                <div className="settings-form-group">
                  <label className="settings-form-label">Email</label>
                  <input
                    type="email"
                    className="settings-form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="store@example.com"
                  />
                </div>
                <div className="settings-form-group full-width">
                  <label className="settings-form-label">Website</label>
                  <input
                    type="url"
                    className="settings-form-input"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://www.example.com"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="settings-card">
            <div className="settings-card-header">
              <h3 className="settings-card-title">Store Manager</h3>
            </div>
            <div className="settings-card-body">
              <div className="settings-form-grid">
                <div className="settings-form-group">
                  <label className="settings-form-label">First Name</label>
                  <input
                    type="text"
                    className="settings-form-input"
                    value={managerFirstName}
                    onChange={(e) => setManagerFirstName(e.target.value)}
                    placeholder="First name"
                  />
                </div>
                <div className="settings-form-group">
                  <label className="settings-form-label">Last Name</label>
                  <input
                    type="text"
                    className="settings-form-input"
                    value={managerLastName}
                    onChange={(e) => setManagerLastName(e.target.value)}
                    placeholder="Last name"
                  />
                </div>
                <div className="settings-form-group">
                  <label className="settings-form-label">Email</label>
                  <input
                    type="email"
                    className="settings-form-input"
                    value={managerEmail}
                    onChange={(e) => setManagerEmail(e.target.value)}
                    placeholder="manager@example.com"
                  />
                </div>
                <div className="settings-form-group">
                  <label className="settings-form-label">Phone</label>
                  <input
                    type="tel"
                    className="settings-form-input"
                    value={managerPhone}
                    onChange={(e) => setManagerPhone(e.target.value)}
                    placeholder="(123) 456-7890"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className="settings-action-bar">
        <button
          type="button"
          className="settings-btn secondary"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="location-form"
          className="settings-btn primary"
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
        </button>
        {isEditMode && (
          <button
            type="button"
            className="settings-btn danger"
            onClick={handleDeleteClick}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

export default LocationEditor;
